import { z } from 'zod';
/**
 * Typed per-target release settings: `releaseLanes[].params`.
 *
 * Field names mirror the release engine's target configs (`apps/daemon/src/release/contracts.ts`,
 * minus `kind`, which comes from the lane's `target`). `config-adapter.ts` passes `params`
 * through as the typed target config, enabling the typed recipes and the processing/deploy
 * pollers. Without `params` a lane runs `recipe.commands` as a command lane.
 *
 * Secrets are always `op://` references, never values.
 */
export const OpRef = z
    .string()
    .regex(/^op:\/\/[^/\s](?:[^/\r\n]*[^/\s])?(?:\/[^/\s](?:[^/\r\n]*[^/\s])?){2,3}$/, 'must be a 1Password secret reference: op://<vault>/<item>/[<section>/]<field> (names may contain spaces and dots)')
    .describe('1Password secret reference (op://vault/item/field); never a value');
export const EnvName = z
    .string()
    .regex(/^[A-Z_][A-Z0-9_]*$/, 'must be an UPPER_SNAKE_CASE environment variable name');
const cmds = z.array(z.string().trim().min(1)).min(1);
const optCmds = z.array(z.string().trim().min(1));
const relPath = z.string().min(1);
const secretEnv = z.record(EnvName, OpRef).describe('Env var → op:// ref, injected via `op run`');
const secretFiles = z
    .record(EnvName, OpRef)
    .describe('Env var → op:// ref, materialized as a 0600 temp file whose path is the env value');
export const AscApiKey = z
    .object({
    keyId: z.string().min(1),
    issuerId: z.string().min(1),
    privateKey: OpRef.describe('The .p8 private key'),
})
    .strict();
export const WebVerify = z
    .object({
    url: z.url(),
    header: z.string().min(1).optional(),
    bodyIncludes: z.enum(['commit', 'version']).optional(),
    expect: z.enum(['commit', 'version']).optional(),
})
    .strict();
export const IosUploader = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('xcodebuild-upload') }).strict(),
    z.object({ kind: z.literal('altool') }).strict(),
    z.object({ kind: z.literal('fastlane-pilot') }).strict(),
    z
        .object({
        kind: z.literal('fastlane-lane'),
        lane: z.string().min(1),
        platform: z.string().optional(),
    })
        .strict(),
]);
export const TestflightParams = z
    .object({
    workspace: relPath.optional(),
    project: relPath.optional(),
    scheme: z.string().min(1),
    configuration: z.string().optional(),
    teamId: z.string().optional(),
    exportOptionsPlist: relPath.optional(),
    appId: z.string().regex(/^\d+$/, 'App Store Connect app id is numeric').optional(),
    bundleId: z.string().optional(),
    uploader: IosUploader.default({ kind: 'xcodebuild-upload' }),
    ascKey: AscApiKey,
    preBuild: optCmds.optional(),
    allowProvisioningUpdates: z.boolean().optional(),
    // ---- release-lanes-v2: multi-platform TestFlight ----
    platforms: z
        .array(z.enum(['ios', 'macos']))
        .min(1)
        .optional()
        .describe('Archive + upload per platform (default [ios]); one build number for all'),
    buildNumber: z
        .enum(['commit-count', 'minute', 'none'])
        .optional()
        .describe('commit-count = git rev-list --count (never lower), minute = UTC yyyyMMddHHmm; overrides versionBump'),
    tag: z
        .string()
        .regex(/^[\w.{}-]+$/, 'tag template, e.g. "build-{n}"')
        .optional()
        .describe('git tag created on the landed commit once uploaded; {n} = build number'),
    pushTag: z.boolean().optional().describe('Also push the tag to the remote (default false)'),
    notes: z
        .enum(['tasks', 'none'])
        .optional()
        .describe('"What to Test" from the landed task titles (default tasks)'),
    exportOptionsPlistMac: relPath.optional().describe('Export options for the macOS archive'),
    installOnMac: z
        .boolean()
        .optional()
        .describe('Offer an "Install on this Mac" handoff once the macOS build is available'),
    macAppName: z.string().optional().describe('App to quit before installing (handoff note)'),
    // ---- end release-lanes-v2 ----
})
    .strict()
    .refine((p) => !(p.workspace && p.project), {
    message: 'set either workspace or project, not both',
    path: ['workspace'],
});
export const WebCloudflareParams = z
    .object({
    mode: z.enum(['workers', 'pages']),
    project: z.string().optional(),
    outputDir: relPath.optional(),
    env: z.string().optional(),
    branch: z.string().optional(),
    build: optCmds.optional(),
    cwd: relPath.optional(),
    deployCommand: z.string().optional(),
    apiToken: OpRef.optional(),
    accountId: z.string().optional(),
    verify: WebVerify.optional(),
    deployer: z
        .enum(['wrangler'])
        .optional()
        .describe('Pluggable deploy executor; reads apiToken from 1Password only during the deploy'),
    versionMarker: relPath
        .optional()
        .describe('File written after the build ({commit, version, buildId}) that `verify` probes'),
})
    .strict()
    .refine((p) => p.mode !== 'pages' || (p.project && p.outputDir), {
    message: 'pages mode needs project and outputDir',
    path: ['project'],
});
export const DesktopParams = z
    .object({
    build: cmds,
    artifacts: z.array(z.string().min(1)).min(1, 'list the artifact glob(s) to upload'),
    publish: optCmds.optional(),
    cwd: relPath.optional(),
    secretEnv: secretEnv.optional(),
    secretFiles: secretFiles.optional(),
})
    .strict();
export const PlayInternalParams = z
    .object({
    mode: z.enum(['fastlane-supply', 'gradle-publish']),
    packageName: z.string().optional(),
    module: z.string().optional(),
    aab: relPath.optional(),
    track: z.string().optional(),
    serviceAccountJson: OpRef,
    cwd: relPath.optional(),
})
    .strict();
export const SteamBranchParams = z
    .object({
    appId: z.string().regex(/^\d+$/, 'Steam app id is numeric'),
    appBuildVdf: relPath,
    branch: z.string().min(1),
    username: z.string().min(1).describe('Build account; steamcmd reuses its cached login'),
    build: optCmds.optional(),
    steamcmd: z.string().optional(),
})
    .strict();
export const GarminSideloadParams = z
    .object({
    jungle: relPath,
    device: z.string().min(1),
    output: relPath,
    developerKeyPath: z.string().min(1),
    copyTo: z.string().optional(),
    sideloadCommand: z.string().optional(),
    flags: z.array(z.string()).optional(),
    cwd: relPath.optional(),
    sdkBin: z.string().optional(),
})
    .strict();
export const CustomParams = z
    .object({
    build: cmds,
    upload: optCmds.optional(),
    artifacts: z.array(z.string().min(1)).optional(),
    cwd: relPath.optional(),
    secretEnv: secretEnv.optional(),
    secretFiles: secretFiles.optional(),
    verify: WebVerify.optional(),
    ascProcessing: z
        .object({ appId: z.string().optional(), bundleId: z.string().optional(), ascKey: AscApiKey })
        .strict()
        .optional(),
})
    .strict();
// ---- release-lanes-v2: lane secrets, verify hooks, kamal and script targets ----
/** Relative path that cannot escape its base dir (no absolute, `~`, drive letter or `..`). */
export const SafeRelPath = z
    .string()
    .min(1)
    .refine((p) => !/^([/\\~]|[A-Za-z]:)/.test(p) && !p.split(/[/\\]/).some((s) => s === '..' || s === ''), 'must be a relative path inside the lane secrets dir (no "/", "~", ".." or empty segments)');
/** A secret materialized as a file in the step's private temp dir (removed after the step). */
export const LaneSecretFile = z
    .object({
    ref: OpRef,
    path: SafeRelPath.describe('Path inside $VIKO_SECRETS_DIR, e.g. AuthKey.p8 or kamal/secrets'),
    mode: z
        .enum(['0400', '0600'])
        .default('0600')
        .describe('File permissions; the dir is always 0700'),
    env: EnvName.optional().describe('Env var that receives the absolute file path'),
})
    .strict();
const verifyExpect = z
    .enum(['ok', 'commit', 'version'])
    .describe('ok = 2xx; commit/version = the response proves this build is live');
/**
 * Availability proof: a build is "available" only once this passes. Exactly one of `command`,
 * `script` or `url`. Command/script get VIKO_COMMIT, VIKO_VERSION, VIKO_BUILD_NUMBER and may
 * print `{"version": …, "commit": …}` as their last stdout line to report what is live.
 */
export const LaneVerify = z
    .object({
    command: z.string().trim().min(1).optional(),
    script: relPath.optional(),
    url: z.url().optional(),
    expect: verifyExpect.optional(),
    header: z.string().min(1).optional().describe('Read the expected value from this header'),
    contains: z.string().min(1).optional().describe('Literal the body must contain'),
    timeoutSec: z.number().int().min(5).max(7200).optional(),
    intervalSec: z.number().int().min(1).max(600).optional(),
    skipIfLive: z
        .boolean()
        .optional()
        .describe('Probe before building; if this exact commit/version already verifies, skip to available (default: on for url + expect commit/version)'),
})
    .strict()
    .refine((v) => [v.command, v.script, v.url].filter((x) => x !== undefined).length === 1, {
    message: 'verify needs exactly one of command, script or url',
});
/**
 * Lane-level fields shared by every target (including command-only lanes). Spread into the lane
 * object in schema.ts so they validate the same way for all targets.
 */
export const LANE_V2_FIELDS = {
    secretEnv: z
        .record(EnvName, OpRef)
        .optional()
        .describe('Env var → op:// ref for every step of this lane, read with the 1Password service account'),
    secretFiles: z
        .array(LaneSecretFile)
        .optional()
        .describe('Secrets written to a private 0700 temp dir for each step, removed after it'),
    verify: LaneVerify.optional().describe('Must pass before a build counts as available'),
    resources: z
        .array(z.string().regex(/^[a-z0-9][a-z0-9:._-]*$/, 'resource name, e.g. "ship-webb"'))
        .optional()
        .describe('Machine resources this lane holds while it runs (enforced by the resource manager)'),
    environment: z
        .string()
        .min(1)
        .optional()
        .describe('Label for the "what\'s live" card (default: the lane name)'),
};
const kamalHealth = z
    .object({
    url: z.url(),
    expect: verifyExpect.optional(),
    header: z.string().min(1).optional(),
    contains: z.string().min(1).optional(),
})
    .strict();
/** Kamal 2 deploy (`kamal deploy|setup -d <destination>`). */
export const KamalParams = z
    .object({
    env: z.enum(['staging', 'production']),
    destination: z
        .string()
        .regex(/^[\w-]+$/)
        .optional()
        .describe('kamal -d (default: env)'),
    cwd: relPath.optional().describe('Directory holding config/deploy.yml'),
    command: z.enum(['deploy', 'setup']).default('deploy'),
    args: z.array(z.string().min(1)).optional().describe('Extra kamal args, e.g. --skip-push'),
    version: z
        .enum(['commit', 'none'])
        .default('commit')
        .describe('commit = --version <landed sha>'),
    preDeploy: optCmds.optional().describe('Commands before kamal (e.g. migrations check)'),
    healthCheck: kamalHealth.optional().describe('Becomes the lane verify (availability)'),
    image: z
        .object({
        repository: z.string().min(1).describe('e.g. registry.digitalocean.com/zipmail/relay'),
        dockerfile: relPath.optional(),
        context: relPath.optional(),
    })
        .strict()
        .optional(),
    verifyImage: z
        .object({
        digest: z.boolean().default(true).describe('Resolve and pin the pushed digest'),
        cosignKey: z
            .union([OpRef, relPath])
            .optional()
            .describe('cosign public key (op:// ref or repo path)'),
    })
        .strict()
        .optional(),
    remoteBuilder: z
        .object({
        machine: z
            .string()
            .regex(/^[a-z0-9][a-z0-9-]*$/)
            .optional(),
        host: z.string().min(1).optional().describe('ssh target (default: the machine id)'),
        platform: z.literal('linux/amd64').default('linux/amd64'),
    })
        .strict()
        .optional()
        .describe('Build the image on another machine (x86) instead of under emulation'),
    rollback: z
        .object({
        command: z.string().min(1).optional(),
        auto: z.boolean().default(false).describe('Roll back automatically when verify fails'),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((p) => !(p.remoteBuilder || p.verifyImage) || p.image, {
    message: 'remoteBuilder and verifyImage need image.repository',
    path: ['image'],
});
/** A repo's own ship script (Telltale ship.sh, Escargo ship-testflight.sh) run as a Viko lane. */
export const ScriptParams = z
    .object({
    run: z.string().trim().min(1).describe('Script or command, relative to the repo'),
    args: z.array(z.string()).optional(),
    cwd: relPath.optional(),
    env: z.record(EnvName, z.string()).optional().describe('Non-secret env'),
    artifacts: z.array(z.string().min(1)).optional(),
    timeoutMin: z
        .number()
        .int()
        .min(1)
        .max(24 * 60)
        .optional(),
    alreadyDone: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Idempotency probe: exit 0 means this commit is already shipped, skip the run'),
})
    .strict();
// ---- end release-lanes-v2 ----
/** params schema per `target`. */
export const PARAMS_BY_TARGET = {
    testflight: TestflightParams,
    'web-cloudflare': WebCloudflareParams,
    'mac-desktop': DesktopParams,
    'windows-desktop': DesktopParams,
    'play-internal': PlayInternalParams,
    'steam-branch': SteamBranchParams,
    'garmin-sideload': GarminSideloadParams,
    custom: CustomParams,
    // ---- release-lanes-v2 ----
    kamal: KamalParams,
    script: ScriptParams,
    // ---- end release-lanes-v2 ----
};
//# sourceMappingURL=release-params.js.map